import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Answers are stored and delivered as Markdown; attorneys edit them as rich
 * text. These helpers convert between the two for the node types the answer
 * editor allows (headings, paragraphs, bold/italic/code, links, lists,
 * quotes, rules, line breaks).
 */
export function markdownToHtml(markdown: string) {
  if (!markdown.trim()) return "";
  return renderToStaticMarkup(
    <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
      {markdown}
    </ReactMarkdown>
  );
}

type Mark = { type: string; attrs?: Record<string, any> };
export type DocNode = {
  type: string;
  attrs?: Record<string, any>;
  content?: DocNode[];
  text?: string;
  marks?: Mark[];
};

const markOrder = ["link", "bold", "italic", "code"];
const markKey = (m: Mark) =>
  m.type === "link" ? `link:${m.attrs?.href || ""}` : m.type;
const escapeText = (text: string) => text.replace(/([\\`*_[\]])/g, "\\$1");
// Digits cannot be backslash-escaped, so an ordered-list marker escapes its "." or ")".
const escapeLineStart = (line: string) =>
  /^\d+[.)]\s/.test(line)
    ? line.replace(/^(\d+)([.)])/, "$1\\$2")
    : /^(#{1,6}\s|>|[-+*]\s|---\s*$)/.test(line)
      ? `\\${line}`
      : line;
const linkHref = (href = "") => href.replace(/[\s()]/g, encodeURIComponent);

function open(mark: Mark) {
  return mark.type === "bold"
    ? "**"
    : mark.type === "italic"
      ? "*"
      : mark.type === "code"
        ? "`"
        : "[";
}
function close(mark: Mark) {
  return mark.type === "bold"
    ? "**"
    : mark.type === "italic"
      ? "*"
      : mark.type === "code"
        ? "`"
        : `](${linkHref(mark.attrs?.href)})`;
}

/** Inline content with marks opened and closed around runs, not per text node. */
function inline(nodes: DocNode[] = []) {
  let out = "";
  let active: Mark[] = [];
  const closeTo = (keep: number) => {
    // Emphasis cannot end in whitespace, so move it outside the closers.
    const trailing = out.match(/\s+$/)?.[0] || "";
    out = out.slice(0, out.length - trailing.length);
    while (active.length > keep) out += close(active.pop()!);
    out += trailing;
  };
  for (const node of nodes) {
    if (node.type === "hardBreak") {
      closeTo(0);
      out += "  \n";
      continue;
    }
    if (node.type !== "text" || !node.text) continue;
    const marks = [...(node.marks || [])]
      .filter(m => markOrder.includes(m.type))
      .sort((a, b) => markOrder.indexOf(a.type) - markOrder.indexOf(b.type));
    const keys = marks.map(markKey);
    let keep = 0;
    while (
      keep < active.length &&
      keys.includes(markKey(active[keep])) &&
      markKey(active[keep]) === keys[keep]
    )
      keep++;
    closeTo(keep);
    let text = node.text;
    const opening = marks.slice(active.length);
    if (opening.length) {
      const leading = text.match(/^\s+/)?.[0] || "";
      out += leading;
      text = text.slice(leading.length);
      for (const mark of opening) {
        out += open(mark);
        active.push(mark);
      }
    }
    out += active.some(m => m.type === "code") ? text : escapeText(text);
  }
  closeTo(0);
  return out;
}

const indent = (text: string, spaces: number) =>
  text
    .split("\n")
    .map((line, i) => (i === 0 || !line ? line : " ".repeat(spaces) + line))
    .join("\n");

function block(node: DocNode): string {
  switch (node.type) {
    case "paragraph":
      return inline(node.content)
        .split("\n")
        .map(line => escapeLineStart(line))
        .join("\n");
    case "heading":
      return `${"#".repeat(Math.min(6, Math.max(2, node.attrs?.level || 2)))} ${inline(node.content)}`;
    case "blockquote":
      return blocks(node.content)
        .split("\n")
        .map(line => (line ? `> ${line}` : ">"))
        .join("\n");
    case "bulletList":
      return (node.content || [])
        .map(item => `- ${indent(listItem(item), 2)}`)
        .join("\n");
    case "orderedList": {
      const start = node.attrs?.start || 1;
      return (node.content || [])
        .map((item, i) => {
          const marker = `${start + i}. `;
          return marker + indent(listItem(item), marker.length);
        })
        .join("\n");
    }
    case "horizontalRule":
      return "---";
    case "codeBlock":
      return `\`\`\`\n${(node.content || []).map(n => n.text || "").join("")}\n\`\`\``;
    default:
      return blocks(node.content);
  }
}
function listItem(item: DocNode) {
  // Tight lists: a paragraph followed by nested lists on the next line.
  return (item.content || []).map(block).join("\n");
}
function blocks(nodes: DocNode[] = []) {
  return nodes
    .map(block)
    .filter(text => text !== "")
    .join("\n\n");
}

export function docToMarkdown(doc: DocNode) {
  const text = blocks(doc.content);
  return text ? `${text}\n` : "";
}

/** Empty headings, placeholder text, and unfinished links, found before delivery. */
export function lintAnswer(markdown: string) {
  const blocking: string[] = [];
  const warnings: string[] = [];
  if (/\]\(\s*https?:\/\/\s*\)/.test(markdown) || /\]\(\s*\)/.test(markdown))
    blocking.push("A link has no address.");
  if (/\]\(\s*https?:\/\/(localhost|127\.0\.0\.1)[:/]/.test(markdown))
    blocking.push("A link points to this computer (localhost).");
  for (const placeholder of [
    "Your conclusion, or what additional information is needed to reach one",
    "Source title",
  ])
    if (markdown.includes(placeholder))
      blocking.push(
        `Placeholder text is still in the answer: “${placeholder}”.`
      );
  const lines = markdown.split("\n");
  lines.forEach((line, i) => {
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (!heading) return;
    const rest = lines.slice(i + 1);
    const nextContent = rest.find(l => l.trim());
    if (nextContent === undefined || /^#{1,6}\s/.test(nextContent))
      warnings.push(`“${heading[1].trim()}” has nothing under it.`);
  });
  const words = markdown
    .replace(/[#>*_`[\]()-]/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  if (words < 50)
    warnings.push(
      `The answer is short (${words} words). Check it is complete.`
    );
  return { blocking, warnings };
}
